import { useId } from 'react';

import type { Store } from './useAisles.ts';

interface StoreSwitcherProps {
  stores: Store[];
  selected: Store;
  onSelect: (storeId: string) => void;
}

/**
 * Switches which shop the list is sectioned for.
 *
 * A native <select>, and it switches and nothing else, for the reasons the list switcher
 * gives — adding or arranging a shop lives in the Groups panel, and an action inside a
 * select fires on arrow-key navigation.
 *
 * It shares the switcher row with the list rather than taking a line of its own. The top of
 * this screen is two lines on purpose and every block above the first item is a block that
 * is not shopping; this one costs no height at all.
 *
 * Hidden when there is only one shop, which is most households. Unlike the list switcher
 * that is not merely tidiness: a picker offering one option would be the only visible sign
 * of a feature nobody here is using, sitting above the list for ever.
 */
export function StoreSwitcher({ stores, selected, onSelect }: StoreSwitcherProps) {
  const id = useId();

  if (stores.length < 2) {
    return null;
  }

  return (
    // The same wrapper class as the list switcher, because the caret is drawn on it — a
    // <select> cannot have a ::after of its own.
    <div className="list-switcher list-switcher--store">
      <label className="visually-hidden" htmlFor={id}>
        Shop
      </label>
      <select
        id={id}
        className="list-switcher__select"
        value={selected.id}
        onChange={(event) => onSelect(event.target.value)}
      >
        {stores.map((store) => (
          <option key={store.id} value={store.id}>
            {store.name}
          </option>
        ))}
      </select>
    </div>
  );
}
